import '@ant-design/compatible/assets/index.css';
import { useQuery } from 'react-query';
import React, { useCallback, useEffect, useState } from 'react';

import {
  Tabs,
  Radio,
  Spin,
  Row,
  Typography,
  Select,
  notification,
  message,
  InputNumber,
  Form,
} from 'antd';
import * as finEngine from 'services/finEngine';
import DF from 'DisplayFramework';
import { toCurrency, toRate } from 'utils/utils';
import FormInput from 'components/FormInput';
import ParListSelect from 'components/ParListSelect';
import {
  getFinContractById,
  getFinModificationValidByCtrid,
  getFinModificationByModid,
  FinConElementResponse,
  reschedule,
  getFinContractFutureValueList,
  getFinContractIrvScheduleList,
  getTPOFFees,
} from 'services/fincontract';
import SliderWithInput from 'components/SliderWithInput';
import { FormInstance } from 'antd/lib/form';
import TextArea from 'antd/lib/input/TextArea';

import { PricingResponse } from 'services/pricing';
import { dateToString, shouldUpdateCompare } from 'utils/utils2';
import PricingListFilteredSelect from 'components/PricingListFilteredSelect';
import InputRate from 'components/InputRate';
import FinancialScheduleModal from 'components/FinancialScheduleModal';
import ScheduleButton from 'components/ScheduleButton';
import { debounce, groupBy, isNil } from 'lodash';
import { conElementToElement } from 'pages/ProposalV3/PropDetail/conElementToElement';
import UtinaCard from 'components/UtinaCard';
import InputCurrency from 'components/InputCurrency';
import IrvScheduleTable from 'components/IrvScheduleTable';
import GetLabel from 'DisplayFramework/components/GetLabel';
import { AssetFutureValueScheduleResponse } from 'services/assets';
import ElementsTable from 'pages/ProposalV3/PropDetail/ElementsTable';

const { Text } = Typography;
const { TabPane } = Tabs;

export type TabContractReschedulingProps = {
  objectid: number;
  object: string;
  form: FormInstance;
  setSuccessful?: any;
};

const TabContractRescheduling = ({
  objectid,
  object,
  form,
  setSuccessful,
}: TabContractReschedulingProps) => {
  const [state, setState] = useState({
    isLoading: false,
    isModSelected: false,
    remainDuration: '?',
    outStanding: 0,
    newOutStanding: 0,
    fvamount: 0,
    fvamountend: 0,
    oldRental: 0,
    newRental: 0,
    effectDate: '',
    feesAmount: 0,
    feesFormula: '',
    newElements: [] as FinConElementResponse[],
    cteaprrate: 0,
  });

  const { data: contract, isFetching: isLoading } = useQuery(
    ['getFinContractById', { ctrid: objectid }],
    () => getFinContractById({ ctrid: objectid }).then((res) => res.data ?? {}),
    { refetchOnMount: false, staleTime: 60 * 1000 * 5 }
  );

  const { data: modList, isFetching } = useQuery(
    ['getFinModificationValidByCtrid', { ctrid: objectid }],
    () =>
      getFinModificationValidByCtrid({
        ctrid: objectid,
        modevent: 'RESCHEDULE',
      }).then((res) => res.list),
    { refetchOnMount: true }
  );

  useEffect(() => {
    setSuccessful(isLoading && isFetching);
  }, [isLoading, isFetching]);

  const { data: irvList, isLoading: Loading } = useQuery(
    ['getFinContractIrvScheduleList', { ctrid: objectid }],
    () =>
      getFinContractIrvScheduleList({
        ctrid: objectid,
      }).then((res) => res.list),
    { refetchOnMount: false }
  );

  const { data: fvList } = useQuery(
    ['getFinContractFutureValueList', { ctrid: objectid }],
    () =>
      getFinContractFutureValueList({
        ctrid: objectid,
      }).then((res) => res.list ?? []),
    { refetchOnMount: false }
  );

  const handleFinModSelect = (finmod: number) => {
    if (finmod === undefined) {
      updateState({ isModSelected: false });
      return;
    }

    // //  no-// //
    // // ////

    updateState({ isLoading: true });

    let query = getFinModificationByModid({ modid: finmod });

    query.then((response) => {
      if (response.code !== '200') {
        return;
      }
      const res = response.data;

      form?.setFieldsValue({
        mode: 'REAL',
        effectDate: res.simulParams.filter(
          (item) => item.parcode === 'EFFECTDATE' && item.partype === 'IN'
        )[0].newvalue,
        // Spec Event :
        gpDuration: Number(
          res.simulParams.filter(
            (item) => item.parcode === 'GPDURATION' && item.partype === 'IN'
          )[0].newvalue
        ),

        extendDuration: res.simulParams.filter(
          (item) => item.parcode === 'EXTENDDUR' && item.partype === 'IN'
        )[0].newvalue,

        // new RV :
        newRv: res.simulParams.filter(
          (item) => item.parcode === 'NEWRV' && item.partype === 'IN'
        )[0].newvalue,

        //NEWDUEDATE
        newDueDate: res.simulParams.filter(
          (item) => item.parcode === 'NEWDUEDATE' && item.partype === 'IN'
        )[0].newvalue,

        //DEFINTMET
        defintMethod: res.simulParams.filter(
          (item) => item.parcode === 'DEFINTMET' && item.partype === 'IN'
        )[0].newvalue,
        //DEFINTRATE
        defintNominalRate: res.simulParams.filter(
          (item) => item.parcode === 'DEFINTRATE' && item.partype === 'IN'
        )[0].newvalue,

        //GPMETHOD
        gpMethod: res.simulParams.filter(
          (item) => item.parcode === 'GPMETHOD' && item.partype === 'IN'
        )[0].newvalue,
        //GPPERIOD
        gpPeriodicity: res.simulParams.filter(
          (item) => item.parcode === 'GPPERIOD' && item.partype === 'IN'
        )[0].newvalue,

        //GPRATE
        gpNominalRate: Number(
          res.simulParams.filter(
            (item) => item.parcode === 'GPRATE' && item.partype === 'IN'
          )[0].newvalue
        ),

        //REMDUR
        newRemainingDuration: Number(
          res.simulParams.filter(
            (item) => item.parcode === 'REMDUR' && item.partype === 'IN'
          )[0]?.newvalue
        ),

        //NEWRATE
        newRate: Number(
          res.simulParams.filter(
            (item) => item.parcode === 'NEWRATE' && item.partype === 'IN'
          )[0].newvalue
        ),

        // Fees :

        comment: response.data.modcomment,

        feesFormula: Number(
          res.simulParams.filter(
            (item) => item.parcode === 'FEESFORM' && item.partype === 'IN'
          )[0].newvalue
        ),
        feesAmount: res.simulParams.filter(
          (item) => item.parcode === 'FEESAMNT' && item.partype === 'IN'
        )[0].newvalue,

        modreason: res.simulParams.filter(
          (item) => item.parcode === 'MODREASON' && item.partype === 'IN'
        )[0].newvalue,
      });

      updateState({
        isModSelected: true,
        remainDuration: res.simulParams.find(
          (item) => item.parcode === 'REMDUR' && item.partype === 'IN'
        )?.newvalue,
        outStanding: Number(
          res.simulParams.find(
            (item) => item.parcode === 'OUTSTAND' && item.partype === 'RESULT'
          )?.newvalue
        ),
        oldRental: res.simulParams.find(
          (item) => item.parcode === 'RENTAL' && item.partype === 'RESULT'
        )?.oldvalue,
        newRental: res.simulParams.find(
          (item) => item.parcode === 'RENTAL' && item.partype === 'RESULT'
        )?.newvalue,

        modreason: res.simulParams.filter(
          (item) => item.parcode === 'MODREASON' && item.partype === 'IN'
        )[0].newvalue,
      });

      /* handleEffectDateChange(
        res.simulParams.filter(
          (item) => item.parcode === 'EFFECTDATE' && item.partype === 'IN'
        )[0].newvalue
      );*/

      const targetDate = form?.getFieldValue('effectDate');

      const defintMethod = form?.getFieldValue('defintMethod');
      const newDueDate = form?.getFieldValue('newDueDate');
      const defintNominalRate = form?.getFieldValue('defintNominalRate');

      const gpDuration = form?.getFieldValue('gpDuration');
      const gpMethod = form?.getFieldValue('gpMethod');
      const gpPeriodicity = form?.getFieldValue('gpPeriodicity');
      const gpNominalRate = form?.getFieldValue('gpNominalRate');
      const extendDuration =
        gpMethod === 'NONE' ? true : form?.getFieldValue('extendDuration');

      const inPartialPayoffAmount = form?.getFieldValue(
        'inPartialPayoffAmount'
      );
      const inPartialPayoffFormula = form?.getFieldValue(
        'inPartialPayoffFormula'
      );
      const inPartialPayoffFeesAmount = form?.getFieldValue(
        'inPartialPayoffFeesAmount'
      );

      const newRate = form?.getFieldValue('newRate');

      const newRv = form?.getFieldValue('newRv');

      const newRemainingDuration = form?.getFieldValue('newRemainingDuration');

      calculate(
        objectid,
        newRv ?? rvDefault,
        gpDuration,
        gpMethod,
        gpPeriodicity,
        gpNominalRate,
        extendDuration,
        newRemainingDuration,
        newRate,
        0,
        '',
        targetDate,
        newDueDate,
        defintMethod,
        defintNominalRate,
        inPartialPayoffAmount,
        inPartialPayoffFormula,
        inPartialPayoffFeesAmount
      );
      updateState({ isLoading: false });
    });
  };

  const durationDefault = contract?.conElements?.find(
    (e) => e.ctetype === 'FIN'
  )?.cteduration;

  const rvDefault = contract?.conElements?.find(
    (e) => e.ctetype === 'FIN'
  )?.ctervamount;

  const dueDateDefault = contract?.conElements?.find(
    (e) => e.ctetype === 'FIN'
  )?.ctebilligdate;

  const durationMin = contract?.conElements?.find((e) => e.ctetype === 'FIN')
    ?.pricing?.durationmin;
  const durationMax = contract?.conElements?.find((e) => e.ctetype === 'FIN')
    ?.pricing?.durationmax;

  const notInvoicedSchedule = contract?.conElements
    ?.find((e) => e.ctetype === 'FIN')
    ?.conElemSchedules.filter(
      (s) =>
        s.ciid === null &&
        s.ctstype !== 'UPFRONT' &&
        s.ctstype !== 'RV' &&
        s.ctstype !== 'DEFINT'
    );

  const optionDates = notInvoicedSchedule?.map(
    (e, index) =>
      e.ctstype !== 'UPFRONT' && (
        <option key={index} value={e.ctsduedate.toString()}>
          {e.ctsduedate}
        </option>
      )
  );

  const optionModListValid = modList?.map((e, index) => (
    <option key={index} value={e.modid}>
      {e.modid + ' - ' + e.modcomment}
    </option>
  ));

  const optionsMode = [
    { label: 'Simulation', value: 'SIMUL' },
    { label: 'Real', value: 'REAL' },
  ];

  const optionsExtend = [
    { label: 'Yes', value: true },
    { label: 'No', value: false },
  ];
  const updateState = useCallback(
    (values) => {
      setState((prev) => ({ ...prev, ...values }));
    },
    [setState]
  );

  const handleinPPayoffFeesAmountChange = (val: any) => {
    const targetDate = form?.getFieldValue('effectDate');

    // //  no-// //
    // ////

    if (targetDate === '' || targetDate === undefined) {
      notification.error({ message: 'Please select a date' });
      return;
    }

    //const defintMethod = form?.getFieldValue('defintMethod');
    const newDueDate = form?.getFieldValue('newDueDate');
    const newRv = form?.getFieldValue('newRv');
    const defintNominalRate = form?.getFieldValue('defintNominalRate');
    const gpDuration = form?.getFieldValue('gpDuration');
    const gpMethod = form?.getFieldValue('gpMethod');
    const gpPeriodicity = form?.getFieldValue('gpPeriodicity');
    const gpNominalRate = form?.getFieldValue('gpNominalRate');
    const extendDuration =
      gpMethod === 'NONE' ? true : form?.getFieldValue('extendDuration');
    //const newRemainingDuration = form?.getFieldValue('newRemainingDuration');
    const newRate = form?.getFieldValue('newRate');

    const inPartialPayoffAmount = form?.getFieldValue('inPartialPayoffAmount');
    const inPartialPayoffFormula = form?.getFieldValue(
      'inPartialPayoffFormula'
    );
    const inPartialPayoffFeesAmount = form?.getFieldValue(
      'inPartialPayoffFeesAmount'
    );
    const defintMethod = form?.getFieldValue('defintMethod');
    const remainDuration = notInvoicedSchedule?.filter(
      (e) => e.ctsduedate >= targetDate
    ).length;
    const fremainDuration = remainDuration !== undefined ? remainDuration : 0;

    let newRemainingDuration = form?.getFieldValue('newRemainingDuration');

    if (extendDuration) {
      newRemainingDuration = fremainDuration + gpDuration;
      form?.setFieldsValue({ newRemainingDuration: newRemainingDuration });
    } else {
      newRemainingDuration = fremainDuration;
      form?.setFieldsValue({ newRemainingDuration: newRemainingDuration });
    }

    calculate(
      objectid,
      newRv,
      gpDuration,
      gpMethod,
      gpPeriodicity,
      gpNominalRate,
      extendDuration,
      newRemainingDuration,
      newRate,
      0,
      '',
      targetDate,
      newDueDate,
      defintMethod,
      0,
      inPartialPayoffAmount,
      inPartialPayoffFormula,
      val
    );
  };

  const handleinPPayoffAmountChange = (val: any) => {
    const targetDate = form?.getFieldValue('effectDate');

    // //  no-// //
    // ////

    if (targetDate === '' || targetDate === undefined) {
      notification.error({ message: 'Please select a date' });
      return;
    }

    //const defintMethod = form?.getFieldValue('defintMethod');
    const newDueDate = form?.getFieldValue('newDueDate');
    const newRv = form?.getFieldValue('newRv');
    const defintNominalRate = form?.getFieldValue('defintNominalRate');
    const gpDuration = form?.getFieldValue('gpDuration');
    const gpMethod = form?.getFieldValue('gpMethod');
    const gpPeriodicity = form?.getFieldValue('gpPeriodicity');
    const gpNominalRate = form?.getFieldValue('gpNominalRate');
    const extendDuration =
      gpMethod === 'NONE' ? true : form?.getFieldValue('extendDuration');
    //const newRemainingDuration = form?.getFieldValue('newRemainingDuration');
    const newRate = form?.getFieldValue('newRate');

    const inPartialPayoffAmount = form?.getFieldValue('inPartialPayoffAmount');
    const inPartialPayoffFormula = form?.getFieldValue(
      'inPartialPayoffFormula'
    );
    const inPartialPayoffFeesAmount = form?.getFieldValue(
      'inPartialPayoffFeesAmount'
    );
    const defintMethod = form?.getFieldValue('defintMethod');
    const remainDuration = notInvoicedSchedule?.filter(
      (e) => e.ctsduedate >= targetDate
    ).length;
    const fremainDuration = remainDuration !== undefined ? remainDuration : 0;

    let newRemainingDuration = form?.getFieldValue('newRemainingDuration');

    if (extendDuration) {
      newRemainingDuration = fremainDuration + gpDuration;
      form?.setFieldsValue({ newRemainingDuration: newRemainingDuration });
    } else {
      newRemainingDuration = fremainDuration;
      form?.setFieldsValue({ newRemainingDuration: newRemainingDuration });
    }

    calculate(
      objectid,
      newRv,
      gpDuration,
      gpMethod,
      gpPeriodicity,
      gpNominalRate,
      extendDuration,
      newRemainingDuration,
      newRate,
      0,
      '',
      targetDate,
      newDueDate,
      defintMethod,
      0,
      val,
      inPartialPayoffFormula,
      inPartialPayoffFeesAmount
    );
  };

  const handledefintMethodChange = (value: any) => {
    const targetDate = form?.getFieldValue('effectDate');

    // //  no-// //
    // ////

    if (targetDate === '' || targetDate === undefined) {
      notification.error({ message: 'Please select a date' });
      return;
    }

    //const defintMethod = form?.getFieldValue('defintMethod');
    const newDueDate = form?.getFieldValue('newDueDate');
    const newRv = form?.getFieldValue('newRv');
    const defintNominalRate = form?.getFieldValue('defintNominalRate');
    const gpDuration = form?.getFieldValue('gpDuration');
    const gpMethod = form?.getFieldValue('gpMethod');
    const gpPeriodicity = form?.getFieldValue('gpPeriodicity');
    const gpNominalRate = form?.getFieldValue('gpNominalRate');
    const extendDuration =
      gpMethod === 'NONE' ? true : form?.getFieldValue('extendDuration');
    //const newRemainingDuration = form?.getFieldValue('newRemainingDuration');
    const newRate = form?.getFieldValue('newRate');

    const inPartialPayoffAmount = form?.getFieldValue('inPartialPayoffAmount');
    const inPartialPayoffFormula = form?.getFieldValue(
      'inPartialPayoffFormula'
    );
    const inPartialPayoffFeesAmount = form?.getFieldValue(
      'inPartialPayoffFeesAmount'
    );

    const remainDuration = notInvoicedSchedule?.filter(
      (e) => e.ctsduedate >= targetDate
    ).length;
    const fremainDuration = remainDuration !== undefined ? remainDuration : 0;

    let newRemainingDuration = form?.getFieldValue('newRemainingDuration');

    if (extendDuration) {
      newRemainingDuration = fremainDuration + gpDuration;
      form?.setFieldsValue({ newRemainingDuration: newRemainingDuration });
    } else {
      newRemainingDuration = fremainDuration;
      form?.setFieldsValue({ newRemainingDuration: newRemainingDuration });
    }

    calculate(
      objectid,
      newRv,
      gpDuration,
      gpMethod,
      gpPeriodicity,
      gpNominalRate,
      extendDuration,
      newRemainingDuration,
      newRate,
      0,
      '',
      targetDate,
      newDueDate,
      value,
      defintNominalRate,
      inPartialPayoffAmount,
      inPartialPayoffFormula,
      inPartialPayoffFeesAmount
    );
  };

  const handledefintNominalRateChange = (value: any) => {
    const targetDate = form?.getFieldValue('effectDate');

    if (targetDate === '' || targetDate === undefined) {
      notification.error({ message: 'Please select a date' });
      return;
    }

    // //  no-// //
    // ////

    const newRv = form?.getFieldValue('newRv');
    const defintMethod = form?.getFieldValue('defintMethod');
    const newDueDate = form?.getFieldValue('newDueDate');
    //const defintNominalRate = form?.getFieldValue('defintNominalRate');
    const gpDuration = form?.getFieldValue('gpDuration');
    const gpMethod = form?.getFieldValue('gpMethod');
    const gpPeriodicity = form?.getFieldValue('gpPeriodicity');
    const gpNominalRate = form?.getFieldValue('gpNominalRate');
    const extendDuration =
      gpMethod === 'NONE' ? true : form?.getFieldValue('extendDuration');
    //const newRemainingDuration = form?.getFieldValue('newRemainingDuration');
    const newRate = form?.getFieldValue('newRate');

    const inPartialPayoffAmount = form?.getFieldValue('inPartialPayoffAmount');
    const inPartialPayoffFormula = form?.getFieldValue(
      'inPartialPayoffFormula'
    );
    const inPartialPayoffFeesAmount = form?.getFieldValue(
      'inPartialPayoffFeesAmount'
    );

    const remainDuration = notInvoicedSchedule?.filter(
      (e) => e.ctsduedate >= targetDate
    ).length;
    const fremainDuration = remainDuration !== undefined ? remainDuration : 0;

    let newRemainingDuration = form?.getFieldValue('newRemainingDuration');

    if (extendDuration) {
      newRemainingDuration = fremainDuration + gpDuration;
      form?.setFieldsValue({ newRemainingDuration: newRemainingDuration });
    } else {
      newRemainingDuration = fremainDuration;
      form?.setFieldsValue({ newRemainingDuration: newRemainingDuration });
    }

    calculate(
      objectid,
      newRv,
      gpDuration,
      gpMethod,
      gpPeriodicity,
      gpNominalRate,
      extendDuration,
      newRemainingDuration,
      newRate,
      0,
      '',
      targetDate,
      newDueDate,
      defintMethod,
      value,
      inPartialPayoffAmount,
      inPartialPayoffFormula,
      inPartialPayoffFeesAmount
    );
  };

  const handleRvChange = (value: any) => {
    const targetDate = form?.getFieldValue('effectDate');

    if (targetDate === '' || targetDate === undefined) {
      notification.error({ message: 'Please select a date' });
      return;
    }

    //const newRv = form?.getFieldValue('newRv');
    const defintMethod = form?.getFieldValue('defintMethod');
    const newDueDate = form?.getFieldValue('newDueDate');
    const defintNominalRate = form?.getFieldValue('defintNominalRate');
    const gpDuration = form?.getFieldValue('gpDuration');
    const gpMethod = form?.getFieldValue('gpMethod');
    const gpPeriodicity = form?.getFieldValue('gpPeriodicity');
    const gpNominalRate = form?.getFieldValue('gpNominalRate');
    const extendDuration =
      gpMethod === 'NONE' ? true : form?.getFieldValue('extendDuration');
    //const newRemainingDuration = form?.getFieldValue('newRemainingDuration');
    const newRate = form?.getFieldValue('newRate');

    const inPartialPayoffAmount = form?.getFieldValue('inPartialPayoffAmount');
    const inPartialPayoffFormula = form?.getFieldValue(
      'inPartialPayoffFormula'
    );
    const inPartialPayoffFeesAmount = form?.getFieldValue(
      'inPartialPayoffFeesAmount'
    );

    const remainDuration = notInvoicedSchedule?.filter(
      (e) => e.ctsduedate >= targetDate
    ).length;
    const fremainDuration = remainDuration !== undefined ? remainDuration : 0;

    let newRemainingDuration = form?.getFieldValue('newRemainingDuration');

    calculate(
      objectid,
      value,
      gpDuration,
      gpMethod,
      gpPeriodicity,
      gpNominalRate,
      extendDuration,
      newRemainingDuration,
      newRate,
      0,
      '',
      targetDate,
      newDueDate,
      defintMethod,
      defintNominalRate,
      inPartialPayoffAmount,
      inPartialPayoffFormula,
      inPartialPayoffFeesAmount
    );
  };
  const handleDueDateChange = (value: any) => {
    const targetDate = form?.getFieldValue('effectDate');

    if (targetDate === '' || targetDate === undefined) {
      notification.error({ message: 'Please select a date' });
      return;
    }
    const newRv = form?.getFieldValue('newRv');
    const defintMethod = form?.getFieldValue('defintMethod');
    //const newDueDate = form?.getFieldValue('newDueDate');
    const defintNominalRate = form?.getFieldValue('defintNominalRate');
    const gpDuration = form?.getFieldValue('gpDuration');
    const gpMethod = form?.getFieldValue('gpMethod');
    const gpPeriodicity = form?.getFieldValue('gpPeriodicity');
    const gpNominalRate = form?.getFieldValue('gpNominalRate');
    const extendDuration =
      gpMethod === 'NONE' ? true : form?.getFieldValue('extendDuration');
    //const newRemainingDuration = form?.getFieldValue('newRemainingDuration');
    const newRate = form?.getFieldValue('newRate');

    const remainDuration = notInvoicedSchedule?.filter(
      (e) => e.ctsduedate >= targetDate
    ).length;

    const fremainDuration = remainDuration !== undefined ? remainDuration : 0;

    let newRemainingDuration = form?.getFieldValue('newRemainingDuration');

    if (extendDuration) {
      newRemainingDuration = fremainDuration + gpDuration;
      form?.setFieldValue('newRemainingDuration', newRemainingDuration);
    } else {
      newRemainingDuration = fremainDuration;
      form?.setFieldValue('newRemainingDuration', newRemainingDuration);
    }

    calculate(
      objectid,
      newRv,
      gpDuration,
      gpMethod,
      gpPeriodicity,
      gpNominalRate,
      extendDuration,
      newRemainingDuration,
      newRate,
      0,
      '',
      targetDate,
      value,
      defintMethod,
      defintNominalRate,
      0,
      '',
      0
    );
  };

  const handleGpDurationChange = (value: any) => {
    const targetDate = form?.getFieldValue('effectDate');

    if (targetDate === '' || targetDate === undefined) {
      notification.error({ message: 'Please select a date' });
      return;
    }

    const newRv = form?.getFieldValue('newRv');
    const defintMethod = form?.getFieldValue('defintMethod');
    const newDueDate = form?.getFieldValue('newDueDate');
    const defintNominalRate = form?.getFieldValue('defintNominalRate');

    const gpMethod = form?.getFieldValue('gpMethod');
    const gpPeriodicity = form?.getFieldValue('gpPeriodicity');
    const gpNominalRate = form?.getFieldValue('gpNominalRate');
    const extendDuration =
      gpMethod === 'NONE' ? true : form?.getFieldValue('extendDuration');
    //const newRemainingDuration = form?.getFieldValue('newRemainingDuration');
    const newRate = form?.getFieldValue('newRate');

    const inPartialPayoffAmount = form?.getFieldValue('inPartialPayoffAmount');
    const inPartialPayoffFormula = form?.getFieldValue(
      'inPartialPayoffFormula'
    );
    const inPartialPayoffFeesAmount = form?.getFieldValue(
      'inPartialPayoffFeesAmount'
    );

    const remainDuration = notInvoicedSchedule?.filter(
      (e) => e.ctsduedate >= targetDate
    ).length;

    const fremainDuration = remainDuration !== undefined ? remainDuration : 0;

    let newRemainingDuration = form?.getFieldValue('newRemainingDuration');

    if (extendDuration) {
      newRemainingDuration = fremainDuration + value;
      form?.setFieldsValue({ newRemainingDuration: newRemainingDuration });
    } else {
      newRemainingDuration = fremainDuration;
      form?.setFieldsValue({ newRemainingDuration: newRemainingDuration });
    }

    calculate(
      objectid,
      newRv,
      value,
      gpMethod,
      gpPeriodicity,
      gpNominalRate,
      extendDuration,
      newRemainingDuration,
      newRate,
      0,
      '',
      targetDate,
      newDueDate,
      defintMethod,
      defintNominalRate,
      inPartialPayoffAmount,
      inPartialPayoffFormula,
      inPartialPayoffFeesAmount
    );
  };

  const handleEffectDateChange = (value: any) => {
    const targetDate = form?.getFieldValue('effectDate');

    if (targetDate === '' || targetDate === undefined) {
      notification.error({ message: 'Please select a date' });
      return;
    }

    const defintMethod = form?.getFieldValue('defintMethod');
    const newDueDate = form?.getFieldValue('newDueDate');
    const defintNominalRate = form?.getFieldValue('defintNominalRate');

    const gpDuration = form?.getFieldValue('gpDuration');
    const gpMethod = form?.getFieldValue('gpMethod');
    const gpPeriodicity = form?.getFieldValue('gpPeriodicity');
    const gpNominalRate = form?.getFieldValue('gpNominalRate');
    const extendDuration =
      gpMethod === 'NONE' ? true : form?.getFieldValue('extendDuration');

    const inPartialPayoffAmount = form?.getFieldValue('inPartialPayoffAmount');
    const inPartialPayoffFormula = form?.getFieldValue(
      'inPartialPayoffFormula'
    );
    const inPartialPayoffFeesAmount = form?.getFieldValue(
      'inPartialPayoffFeesAmount'
    );

    const newRate = form?.getFieldValue('newRate');

    const remainDuration = notInvoicedSchedule?.filter(
      (e) => e.ctsduedate >= value
    ).length;

    const fremainDuration = remainDuration !== undefined ? remainDuration : 0;

    const outstanding = notInvoicedSchedule?.find(
      (e) => e.ctsduedate === value
    )?.ctsstartamount;

    const fvvalue: Array<AssetFutureValueScheduleResponse> =
      fvList?.filter((e) => e?.prcode === 'LEASING')[0]?.fvSchedule ?? [];

    const fvamount = fvvalue?.find((e) => e.fvdate === value)?.fvamount;

    const fvamountend = fvvalue[fvvalue.length - 1]?.fvamount;

    const oldRental = notInvoicedSchedule?.find(
      (e) => e.ctsduedate === value
    )?.ctsrentalamount;

    state.fvamount = fvamount ?? 0;
    state.fvamountend = fvamountend ?? 0;
    state.remainDuration = String(fremainDuration);
    state.outStanding = outstanding ?? 0;
    state.newOutStanding = outstanding ?? 0;
    state.oldRental = oldRental ?? 0;

    const newState = state;
    setState(state);

    let newRemainingDuration = form.getFieldValue('newRemainingDuration');

    // ////

    if (extendDuration) {
      newRemainingDuration = fremainDuration + gpDuration;
      form?.setFieldsValue({ newRemainingDuration: newRemainingDuration });
    } else {
      newRemainingDuration = fremainDuration;
      form?.setFieldsValue({ newRemainingDuration: newRemainingDuration });
    }
    form?.setFieldsValue({ newRv: rvDefault ?? 0 });


    calculate(
      objectid,
      rvDefault ?? 0,
      gpDuration,
      gpMethod,
      gpPeriodicity,
      gpNominalRate,
      extendDuration,
      newRemainingDuration,
      newRate,
      0,
      '',
      targetDate,
      newDueDate,
      defintMethod,
      defintNominalRate,
      inPartialPayoffAmount,
      inPartialPayoffFormula,
      inPartialPayoffFeesAmount
    );
  };

  const handleGpMethodChange = (value: any) => {
    const targetDate = form?.getFieldValue('effectDate');

    if (targetDate === '' || targetDate === undefined) {
      notification.error({ message: 'Please select a date' });
      return;
    }
    const newRv = form?.getFieldValue('newRv');
    const defintMethod = form?.getFieldValue('defintMethod');
    const newDueDate = form?.getFieldValue('newDueDate');
    const defintNominalRate = form?.getFieldValue('defintNominalRate');

    const gpDuration = form?.getFieldValue('gpDuration');

    const gpPeriodicity = form?.getFieldValue('gpPeriodicity');
    const gpNominalRate = form?.getFieldValue('gpNominalRate');

    const gpMethod = form?.getFieldValue('gpMethod');

    const extendDuration =
      gpMethod === 'NONE' ? true : form?.getFieldValue('extendDuration');
    const inPartialPayoffAmount = form?.getFieldValue('inPartialPayoffAmount');
    const inPartialPayoffFormula = form?.getFieldValue(
      'inPartialPayoffFormula'
    );
    const inPartialPayoffFeesAmount = form?.getFieldValue(
      'inPartialPayoffFeesAmount'
    );

    const newRate = form?.getFieldValue('newRate');

    const remainDuration = notInvoicedSchedule?.filter(
      (e) => e.ctsduedate >= targetDate
    ).length;

    const fremainDuration = remainDuration !== undefined ? remainDuration : 0;

    let newRemainingDuration = form?.getFieldValue('newRemainingDuration');
    if (extendDuration && value !== 'NONE') {
      newRemainingDuration = fremainDuration + gpDuration;
      form?.setFieldsValue({ newRemainingDuration: newRemainingDuration });
    } else {
      newRemainingDuration = fremainDuration;
      form?.setFieldsValue({ newRemainingDuration: newRemainingDuration });
    }

    calculate(
      objectid,
      newRv,
      gpDuration,
      value,
      gpPeriodicity,
      gpNominalRate,
      extendDuration,
      newRemainingDuration,
      newRate,
      0,
      '',
      targetDate,
      newDueDate,
      defintMethod,
      defintNominalRate,
      inPartialPayoffAmount,
      inPartialPayoffFormula,
      inPartialPayoffFeesAmount
    );
  };

  const handleGpNominalRateChange = (value: any) => {
    const targetDate = form?.getFieldValue('effectDate');

    if (targetDate === '' || targetDate === undefined) {
      notification.error({ message: 'Please select a date' });
      return;
    }

    const defintMethod = form?.getFieldValue('defintMethod');
    const newDueDate = form?.getFieldValue('newDueDate');
    const defintNominalRate = form?.getFieldValue('defintNominalRate');

    const gpDuration = form?.getFieldValue('gpDuration');
    const gpMethod = form?.getFieldValue('gpMethod');
    const gpPeriodicity = form?.getFieldValue('gpPeriodicity');
    //const gpNominalRate=form?.getFieldValue('gpNominalRate');
    const extendDuration =
      gpMethod === 'NONE' ? true : form?.getFieldValue('extendDuration');
    const newRv = form?.getFieldValue('newRv');
    const newRate = form?.getFieldValue('newRate');

    const inPartialPayoffAmount = form?.getFieldValue('inPartialPayoffAmount');
    const inPartialPayoffFormula = form?.getFieldValue(
      'inPartialPayoffFormula'
    );
    const inPartialPayoffFeesAmount = form?.getFieldValue(
      'inPartialPayoffFeesAmount'
    );

    const remainDuration = notInvoicedSchedule?.filter(
      (e) => e.ctsduedate >= targetDate
    ).length;

    const fremainDuration = remainDuration !== undefined ? remainDuration : 0;

    let newRemainingDuration = form?.getFieldValue('newRemainingDuration');

    if (extendDuration) {
      newRemainingDuration = fremainDuration + gpDuration;
      form?.setFieldsValue({ newRemainingDuration: newRemainingDuration });
    } else {
      newRemainingDuration = fremainDuration;
      form?.setFieldsValue({ newRemainingDuration: newRemainingDuration });
    }

    calculate(
      objectid,
      newRv,
      gpDuration,
      gpMethod,
      gpPeriodicity,
      value,
      extendDuration,
      newRemainingDuration,
      newRate,
      0,
      '',
      targetDate,
      newDueDate,
      defintMethod,
      defintNominalRate,
      inPartialPayoffAmount,
      inPartialPayoffFormula,
      inPartialPayoffFeesAmount
    );
  };

  const handleExtendDurationChange = (value: any) => {
    const targetDate = form?.getFieldValue('effectDate');

    if (targetDate === '' || targetDate === undefined) {
      notification.error({ message: 'Please select a date' });
      return;
    }

    const defintMethod = form?.getFieldValue('defintMethod');
    const newDueDate = form?.getFieldValue('newDueDate');
    const defintNominalRate = form?.getFieldValue('defintNominalRate');
    const newRv = form?.getFieldValue('newRv');
    const gpDuration = form?.getFieldValue('gpDuration');
    const gpMethod = form?.getFieldValue('gpMethod');
    const gpPeriodicity = form?.getFieldValue('gpPeriodicity');
    const gpNominalRate = form?.getFieldValue('gpNominalRate');

    const newRate = form?.getFieldValue('newRate');

    const inPartialPayoffAmount = form?.getFieldValue('inPartialPayoffAmount');
    const inPartialPayoffFormula = form?.getFieldValue(
      'inPartialPayoffFormula'
    );
    const inPartialPayoffFeesAmount = form?.getFieldValue(
      'inPartialPayoffFeesAmount'
    );

    const remainDuration =
      notInvoicedSchedule?.filter((e) => e.ctsduedate >= targetDate).length ??
      0;

    const fremainDuration = remainDuration !== undefined ? remainDuration : 0;

    let newRemainingDuration = Number(
      form?.getFieldValue('newRemainingDuration')
    );

    if (value.target.value) {
      newRemainingDuration = Number(fremainDuration) + gpDuration;

      form?.setFieldsValue({ newRemainingDuration: newRemainingDuration });
    } else {
      newRemainingDuration = fremainDuration;

      form?.setFieldsValue({ newRemainingDuration: newRemainingDuration });
    }

    calculate(
      objectid,
      newRv,
      gpDuration,
      gpMethod,
      gpPeriodicity,
      gpNominalRate,
      value.target.value,
      newRemainingDuration,
      newRate,
      0,
      '',
      targetDate,
      newDueDate,
      defintMethod,
      defintNominalRate,
      inPartialPayoffAmount,
      inPartialPayoffFormula,
      inPartialPayoffFeesAmount
    );
  };

  const handleNewRemainingDurationChange = (value: any) => {
    const targetDate = form?.getFieldValue('effectDate');

    if (targetDate === '' || targetDate === undefined) {
      notification.error({ message: 'Please select a date' });
      return;
    }

    const defintMethod = form?.getFieldValue('defintMethod');
    const newDueDate = form?.getFieldValue('newDueDate');
    const defintNominalRate = form?.getFieldValue('defintNominalRate');
    const newRv = form?.getFieldValue('newRv');
    const gpDuration = form?.getFieldValue('gpDuration');
    const gpMethod = form?.getFieldValue('gpMethod');
    const gpPeriodicity = form?.getFieldValue('gpPeriodicity');
    const gpNominalRate = form?.getFieldValue('gpNominalRate');
    const extendDuration =
      gpMethod === 'NONE' ? true : form?.getFieldValue('extendDuration');

    const newRate = form?.getFieldValue('newRate');

    const inPartialPayoffAmount = form?.getFieldValue('inPartialPayoffAmount');
    const inPartialPayoffFormula = form?.getFieldValue(
      'inPartialPayoffFormula'
    );
    const inPartialPayoffFeesAmount = form?.getFieldValue(
      'inPartialPayoffFeesAmount'
    );

    const remainDuration = notInvoicedSchedule?.filter(
      (e) => e.ctsduedate >= targetDate
    ).length;
    const fremainDuration = remainDuration !== undefined ? remainDuration : 0;

    let newRemainingDuration = value;

    if (extendDuration) {
      newRemainingDuration = value;
    } else {
      newRemainingDuration = fremainDuration;
      form?.setFieldsValue({ newRemainingDuration: newRemainingDuration });
    }

    calculate(
      objectid,
      newRv,
      gpDuration,
      gpMethod,
      gpPeriodicity,
      gpNominalRate,
      extendDuration,
      newRemainingDuration,
      newRate,
      0,
      '',
      targetDate,
      newDueDate,
      defintMethod,
      defintNominalRate,
      inPartialPayoffAmount,
      inPartialPayoffFormula,
      inPartialPayoffFeesAmount
    );
  };

  const handleGpPeriodicityChange = (value: any) => {
    const targetDate = form?.getFieldValue('effectDate');

    if (targetDate === '' || targetDate === undefined) {
      notification.error({ message: 'Please select a date' });
      return;
    }

    const defintMethod = form?.getFieldValue('defintMethod');
    const newDueDate = form?.getFieldValue('newDueDate');
    const defintNominalRate = form?.getFieldValue('defintNominalRate');

    const gpDuration = form?.getFieldValue('gpDuration');
    const gpMethod = form?.getFieldValue('gpMethod');
    //const gpPeriodicity=form?.getFieldValue('gpPeriodicity');
    const gpNominalRate = form?.getFieldValue('gpNominalRate');
    const extendDuration = form?.getFieldValue('extendDuration');
    const newRv = form?.getFieldValue('newRv');
    const newRate = form?.getFieldValue('newRate');

    const inPartialPayoffAmount = form?.getFieldValue('inPartialPayoffAmount');
    const inPartialPayoffFormula = form?.getFieldValue(
      'inPartialPayoffFormula'
    );
    const inPartialPayoffFeesAmount = form?.getFieldValue(
      'inPartialPayoffFeesAmount'
    );

    const remainDuration = notInvoicedSchedule?.filter(
      (e) => e.ctsduedate >= targetDate
    ).length;

    const fremainDuration = remainDuration !== undefined ? remainDuration : 0;

    let newRemainingDuration = form?.getFieldValue('newRemainingDuration');

    if (extendDuration) {
      newRemainingDuration = fremainDuration + gpDuration;
      form?.setFieldsValue({ newRemainingDuration: newRemainingDuration });
    } else {
      newRemainingDuration = fremainDuration;
      form?.setFieldsValue({ newRemainingDuration: newRemainingDuration });
    }

    calculate(
      objectid,
      newRv,
      gpDuration,
      gpMethod,
      value,
      gpNominalRate,
      extendDuration,
      newRemainingDuration,
      newRate,
      0,
      '',
      targetDate,
      newDueDate,
      defintMethod,
      defintNominalRate,
      inPartialPayoffAmount,
      inPartialPayoffFormula,
      inPartialPayoffFeesAmount
    );
  };

  const handleNewRateChange = (value: any) => {
    const targetDate = form?.getFieldValue('effectDate');

    if (targetDate === '' || targetDate === undefined) {
      notification.error({ message: 'Please select a date' });
      return;
    }
    const defintMethod = form?.getFieldValue('defintMethod');
    const newDueDate = form?.getFieldValue('newDueDate');
    const defintNominalRate = form?.getFieldValue('defintNominalRate');
    const newRv = form?.getFieldValue('newRv');
    const gpDuration = form?.getFieldValue('gpDuration');
    const gpMethod = form?.getFieldValue('gpMethod');
    const gpPeriodicity = form?.getFieldValue('gpPeriodicity');
    const gpNominalRate = form?.getFieldValue('gpNominalRate');
    const extendDuration = form?.getFieldValue('extendDuration');

    const inPartialPayoffAmount = form?.getFieldValue('inPartialPayoffAmount');
    const inPartialPayoffFormula = form?.getFieldValue(
      'inPartialPayoffFormula'
    );
    const inPartialPayoffFeesAmount = form?.getFieldValue(
      'inPartialPayoffFeesAmount'
    );

    const remainDuration = notInvoicedSchedule?.filter(
      (e) => e.ctsduedate >= targetDate
    ).length;
    const fremainDuration = remainDuration !== undefined ? remainDuration : 0;

    let newRemainingDuration = form?.getFieldValue('newRemainingDuration');

    if (extendDuration) {
      newRemainingDuration = fremainDuration + gpDuration;
      form?.setFieldsValue({ newRemainingDuration: newRemainingDuration });
    } else {
      newRemainingDuration = fremainDuration;
      form?.setFieldsValue({ newRemainingDuration: newRemainingDuration });
    }

    calculate(
      objectid,
      newRv,
      gpDuration,
      gpMethod,
      gpPeriodicity,
      gpNominalRate,
      extendDuration,
      newRemainingDuration,
      value,
      0,
      '',
      targetDate,
      newDueDate,
      defintMethod,
      defintNominalRate,
      inPartialPayoffAmount,
      inPartialPayoffFormula,
      inPartialPayoffFeesAmount
    );
  };

  const calculateFees = (pricing?: PricingResponse) => {
    finEngine
      .calculateServiceWithStep({
        servReq: {
          finSchedule: contract?.conElements[0].conElemSchedules ?? [],
          inDownPayment: 0,
          inFinAmount: contract?.conElements[0].ctefinancedamount ?? 0,
          inFollowGP: true,
          inGPDuration: contract?.conElements[0].ctegraceperiodduration ?? 0,
          inPstype: pricing?.ctecategory ?? '',
          inRoudingAmount: 2,
          inServcalculationmethod:
            pricing?.serviceResponse.servcalculationmethod ?? '',
          inServfixedamount: pricing?.serviceResponse.servfixedamount ?? 0,
          inServiceduration: pricing?.serviceResponse.servduration ?? '',
          inServpercentage: pricing?.serviceResponse.servpercentage ?? 0,
          inTaxcode: pricing?.taxcode ?? '',
          inTaxrate: 20,
          inDuration: contract?.conElements[0].cteduration ?? 0,
          inInvestAmount: contract?.conElements[0].cteinvestamount ?? 0,
          inAssetAmount: contract?.assets[0].assetnetamount ?? 0,
          inServperiodicity: pricing?.serviceResponse.servperiodicity ?? '1',
          inServDurationFree: pricing?.serviceResponse.servdurationdefault ?? 1,
          inStartDate: dateToString(form?.getFieldValue('effectDate')),
          inServbasis: pricing?.serviceResponse.servbasis ?? 'FLAT',

          inCalculatedamountbasis:
            pricing?.serviceResponse.calculatedamountbasis ?? 'FINANCING',

          inMarginoncalcultedamount:
            pricing?.serviceResponse.marginoncalulatedamount ?? 0,
          inAdditionalcostaftermargin:
            pricing?.serviceResponse.additionalcostaftermargin ?? 0,

          servStep: pricing?.serviceStepList ?? [],
        },
      })
      .then((res) => {
        if (res.success) {
          message.success('Service - ' + res.message);

          form?.setFieldsValue({
            feesAmount: res.installmentamount,
            feesFormula: pricing?.priid,
          });
        }
      });
  };

  const calculateTPOFFees = (pricing?: PricingResponse) => {
    let newRemainingDuration = form?.getFieldValue('newRemainingDuration');

    const effectDate = form?.getFieldValue('effectDate');
    getTPOFFees({
      ctrid: contract?.ctrid ?? 0,
      calculationbasis: pricing?.serviceResponse?.servbasis ?? '',
      duedate: effectDate,
      nbMonths: newRemainingDuration,
    }).then((res) => {
      form?.setFieldsValue({
        inPartialPayoffFeesAmount: res.data,
      });

      handleinPPayoffFeesAmountChange(res.data);
    });
  };
  const [elementID, setElementID] = useState(1);
  const getName = (param: string, cteid: any) => {
    let lcteid = form.getFieldValue('selectedElement');

    if (lcteid === 1) {
      return ['elements', 'FIN', 0, param];
    } else {
      if (form.getFieldValue(['elements', 'FIN'])) {
        const index = form
          .getFieldValue(['elements', 'FIN'])
          .findIndex((item: any) => item.cteid === lcteid);
        return ['elements', 'FIN', index, param];
      } else {
        return ['elements', 'FIN', 0, param];
      }
    }
  };
  const calculate = debounce(
    async (
      ctrid: number,
      newRv: number,
      gpDuration: number,
      gpMethod: string,
      gpPeriodicity: string,
      gpNominalRate: number,
      extendDuration: boolean,
      newRemainingDuration: number,
      newRate: number,
      feesAmount: number,
      feesFormula: string,
      effectDate: string,
      newDueDate: string,
      defintMethod: string,
      defintNominalRate: number,
      inPartialPayoffAmount: number,
      inPartialPayoffFormula: any,
      inPartialPayoffFeesAmount: number
    ) => {
      updateState({ isLoading: true });

      // //  no-// //
      // ////
      await reschedule({
        ctrid: ctrid,
        taskid: 0,
        gpDuration: isNil(gpDuration) ? 0 : gpDuration,
        gpMethod: gpMethod,
        gpPeriodicity: isNil(gpPeriodicity) ? '' : gpPeriodicity,
        gpNominalRate: isNil(gpNominalRate) ? 0 : gpNominalRate,
        extendDuration: isNil(extendDuration) ? true : extendDuration,
        newRemainingDuration:
          isNil(newRemainingDuration) || isNaN(newRemainingDuration)
            ? 0
            : newRemainingDuration,
        newRate: newRate,
        newRv: newRv,
        newDueDate: newDueDate,
        defintMethod: defintMethod,
        defintNominalRate: defintNominalRate,
        modid: 0,
        mode: 'CALCUL',
        invoiceFees: false,
        feesFormula: feesFormula,
        feesAmount: feesAmount,
        effectDate: effectDate,
        inPartialPayoffAmount: inPartialPayoffAmount,
        inPartialPayoffFormula: inPartialPayoffFormula?.priid,
        inPartialPayoffFeesAmount: inPartialPayoffFeesAmount,
      })
        .then((response) => {
          updateState({ isLoading: false });

          if (response.code === '200') {
            notification.success({
              message: response.message,
              duration: 3,
            });

            // //  no-// //
            // ////

            let elements = response.data.conElements;
            let index = elements.findIndex((e) => e.ctetype === 'FIN');
     
            let cteaprrate=elements.find((e) => e.ctetype === 'FIN')?.cteaprrate ?? 0;
            
            let newRental =
              response.data.conElements.find((e) => e.ctetype === 'FIN')
                ?.cterentalamount ?? 0;

            updateState({ newRental: newRental, cteaprrate:cteaprrate });

            elements[index].conElemSchedules = !isNil(
              form?.getFieldValue('conElemSchedules')
            )
              ? form
                  ?.getFieldValue('conElemSchedules')
                  ?.slice(
                    0,
                    form
                      ?.getFieldValue('conElemSchedules')
                      .findIndex(
                        (item: any) =>
                          item.ctsduedate === form?.getFieldValue('effectDate')
                      )
                  )
                  ?.concat(elements[index].conElemSchedules ?? [])
              : [];

            const {
              FIN = [],
              SERV = [],
              FEES = [],
              COMM = [],
              FINSERV = [],
              //      } = groupBy(data.conElements || [], 'ctetype');
            } = groupBy(
              conElementToElement(elements, response.data.offer) || [],
              'ctetype'
            );

            form?.setFieldsValue({
              elements: {
                COMM,
                FEES,
                FIN,
                FINSERV,
                SERV,
              },
            });

            if (inPartialPayoffAmount > 0) {
              // ////

              const newOutstanding = elements[index]?.conElemSchedules?.find(
                (e) => e.ctstype === 'PPAYOFF'
              );

              state.newOutStanding = newOutstanding?.ctsoutstandingamount ?? 0;

              const newState = state;
              setState(newState);
            }

            /*


            */
          } else {
            notification.error({
              message: response.message,
              duration: 0,
            });
          }
        })
        .catch((err) => {
          message.error(
            'Error in calculation. please contact the service provider'
          );
          updateState({ isLoading: false });
        });
    },
    1000
  );

  useEffect(() => {
    if (contract) {
      const elemFin = contract?.conElements?.find((e) => e.ctetype === 'FIN');
      const {
        FIN = [],
        SERV = [],
        FEES = [],
        COMM = [],
        FINSERV = [],
        //      } = groupBy(data.conElements || [], 'ctetype');
      } = groupBy(
        conElementToElement(contract.conElements, contract.offer) || [],
        'ctetype'
      );


      const elements = {
        COMM,
        FEES,
        FIN,
        FINSERV,
        SERV,
      };

      form?.setFieldsValue({
        elements: elements,
        defintMethod: elemFin?.ctedeferredinterestmethod,
        defintNominalRate: elemFin?.ctedeferredinterestnominalrate,
        newDueDate: elemFin?.ctebilligdate,
        gpDuration: 0,
        // gpMethod: 'INV',
        gpPeriodicity: elemFin?.cteperiodicity,
        gpNominalRate: elemFin?.ctenominalrate,
        extendDuration: true,
        newRate: elemFin?.ctenominalrate,
        newRemainingDuration: elemFin?.cteduration,
        mode: 'SIMUL',
        conElemSchedules: contract?.conElements?.find(
          (e) => e.ctetype === 'FIN'
        )?.conElemSchedules,
      });
    }
  }, [contract, form]);
console.log({elementID})
  return (
    <DF.Provider pagcode="tabrescheduling" entity={object} entityid={objectid}>
      <Spin
        spinning={state.isLoading || isLoading}
        tip="Please wait..."
        delay={500}
      >
        <Tabs animated defaultActiveKey="1">
          <TabPane tab={<GetLabel label="FinInfo" />} key={1} forceRender>
            <Row gutter={[8, 8]}>
              <UtinaCard
                level={5}
                comcode="EffectDate"
                styleCard={{
                  color: '#000',
                  paddingTop: 20,
                  paddingBottom: 25,
                }}
                lg={7}
              >
                <Row>
                  <FormInput
                    col
                    xs={20}
                    name="effectDate"
                    rules={[{ required: true }]}
                  >
                    <Select
                      defaultValue=""
                      size="middle"
                      style={{ width: '100%' }}
                      onChange={handleEffectDateChange}
                    >
                      {optionDates}
                    </Select>
                  </FormInput>
                </Row>
                <Row>
                  <FormInput
                    col
                    xs={14}
                    name="mode"
                    rules={[{ required: true }]}
                  >
                    <Radio.Group
                      options={optionsMode}
                      defaultValue="SIMUL"
                      optionType="button"
                      disabled={state.isModSelected}
                    />
                  </FormInput>
                  <FormInput col xs={10} name="modid">
                    <Select
                      size="middle"
                      allowClear
                      style={{ width: '100%' }}
                      onChange={(e: number) => handleFinModSelect(e)}
                      loading={isFetching}
                    >
                      {optionModListValid}
                    </Select>
                  </FormInput>
                </Row>
              </UtinaCard>
              <UtinaCard
                level={5}
                comcode="FeesFormula"
                styleCard={{
                  color: '#000',
                  paddingTop: 20,
                  paddingBottom: 25,
                }}
                lg={5}
              >
                <Row>
                  <FormInput
                    col
                    xs={24}
                    name="feesFormula"
                    //rules={[{ required: true }]}
                  >
                    <PricingListFilteredSelect
                      //disabled={!getFieldValue('offerid')}
                      allowClear
                      disabled={state.isModSelected}
                      params={{
                        offerid: contract?.offerid ?? 0,
                        ctetype: 'MODFEES', // Modifictaion fees
                        prcode: contract?.prcode ?? '',
                        entity: '',
                        entityid: 0,
                        tpid: contract?.tpidclient ?? 0,
                      }}
                      onClear={() => {
                        form?.setFieldValue('feesAmount', 0);
                      }}
                      onChange={(e) => calculateFees(e)}
                    />
                  </FormInput>
                </Row>
                <Row>
                  <FormInput name="feesAmount" shouldUpdate>
                    <InputCurrency
                      currcode={
                        contract?.currcode ??
                        localStorage.getItem('currcode') ??
                        ''
                      }
                      defaultValue={0}
                      disabled={
                        state.isModSelected ||
                        form?.getFieldValue('feesFormula') === undefined
                      }
                    />
                  </FormInput>
                </Row>
              </UtinaCard>

              <UtinaCard
                level={5}
                comcode="SituationAtEffectDate"
                styleCard={{
                  color: '#000',
                  paddingTop: 20,
                  paddingBottom: 25,
                }}
                lg={11}
              >
                <Row gutter={8}>
                  <FormInput col xs={8} name="currentDuration">
                    <Text>{durationDefault}</Text>
                  </FormInput>

                  <FormInput col xs={8} name="remainDuration">
                    <Text>
                      {state.remainDuration + ' / ' + durationDefault}
                    </Text>
                  </FormInput>
                  <FormInput col xs={8} name="oustanding">
                    <Text>
                      {toCurrency(state.outStanding, contract?.currcode)}
                    </Text>
                  </FormInput>
                </Row>
                <Row gutter={8}>
                  <FormInput col xs={8} name="currentRental">
                    <Text strong>
                      {toCurrency(state.oldRental, contract?.currcode)}
                    </Text>
                  </FormInput>
                  <FormInput col xs={8} name="newRental">
                    <Text strong type="danger">
                      {toCurrency(state.newRental, contract?.currcode)}
                    </Text>
                  </FormInput>

                  <FormInput col xs={8} name="newOustanding">
                    <Text strong type="danger">
                      {toCurrency(state.newOutStanding, contract?.currcode)}
                    </Text>
                  </FormInput>

                  <Form.Item
                    shouldUpdate={shouldUpdateCompare([
                      'conElemSchedules',
                      'effectDate',
                      'elements',
                    ])}
                    noStyle
                  >
                    {({ getFieldValue }) => {
                      return (
                        <FinancialScheduleModal
                          comcode="newSchedule"
                          finElement={form?.getFieldValue([
                            'elements',
                            'FIN',
                            0,
                          ])}
                          loading={Loading}
                          currcode={
                            contract?.currcode ??
                            localStorage.getItem('currcode') ??
                            ''
                          }
                          controller={
                            <ScheduleButton
                              size="large"
                              comcode="newSchedule"
                              twoToneColor="green"
                            ></ScheduleButton>
                          }
                        />
                      );
                    }}
                  </Form.Item>
                </Row>
              </UtinaCard>
            </Row>
            <Row gutter={30} style={{ paddingLeft: 10 }}>
              <UtinaCard
                level={5}
                comcode="ChangeBillingDay"
                styleCard={{
                  color: '#000',
                  paddingTop: 10,
                  paddingBottom: 10,
                }}
                lg={12}
              >
                <Row gutter={30}>
                  <FormInput col xs={4} name="currentDueDate">
                    <Text>{dueDateDefault}</Text>
                  </FormInput>
                  <FormInput
                    col
                    xs={5}
                    name="newDueDate"
                    rules={[{ required: true }]}
                  >
                    <ParListSelect
                      disabled={state.isModSelected}
                      parcode="BILLDATE"
                      onSelected={handleDueDateChange}
                    />
                  </FormInput>
                  <FormInput
                    col
                    xs={8}
                    name="defintMethod"
                    rules={[{ required: true }]}
                  >
                    <ParListSelect
                      parcode="CTEDEFINT"
                      disabled={state.isModSelected}
                      onSelected={handledefintMethodChange}
                    />
                  </FormInput>
                  <Form.Item
                    shouldUpdate={shouldUpdateCompare('defintMethod')}
                    noStyle
                  >
                    {({ getFieldValue }) =>
                      getFieldValue('defintMethod') !== 'NONE' && (
                        <FormInput
                          col
                          xs={7}
                          name="defintNominalRate"
                          rules={[{ required: true }]}
                        >
                          <InputRate
                            style={{ width: '100%' }}
                            disabled={state.isModSelected}
                            onChange={handledefintNominalRateChange}
                          />
                        </FormInput>
                      )
                    }
                  </Form.Item>
                </Row>
              </UtinaCard>

              <UtinaCard
                level={5}
                comcode="ChangeRv"
                styleCard={{
                  color: '#000',
                  paddingTop: 10,
                  paddingBottom: 10,
                }}
                lg={11}
              >
                <Row gutter={8}>
                  <FormInput col xs={12} name="currentRv">
                    <Text strong type="danger">
                      {toCurrency(rvDefault, contract?.currcode)}
                    </Text>
                  </FormInput>

                  <FormInput col xs={12} name="fvAtEnd">
                    <Text strong>
                      {toCurrency(state.fvamountend, contract?.currcode)}
                    </Text>
                  </FormInput>
                  <FormInput col xs={12} name="currentFv">
                    <Text strong type="danger">
                      {toCurrency(state.fvamount, contract?.currcode)}
                    </Text>
                  </FormInput>
                  <FormInput
                    col
                    xs={12}
                    name="newRv"
                    rules={[{ required: true }]}
                  >
                    <InputCurrency
                      currcode={
                        contract?.currcode ??
                        localStorage.getItem('currcode') ??
                        ''
                      }
                      disabled={state.isModSelected}
                      onChange={handleRvChange}
                    />
                  </FormInput>
                </Row>
              </UtinaCard>

              <UtinaCard
                level={5}
                comcode="partialPayoff"
                styleCard={{
                  color: '#000',
                  paddingTop: 10,
                  paddingBottom: 10,
                }}
                lg={12}
              >
                <Row gutter={30}>
                  <FormInput col xs={8} name="inPartialPayoffAmount">
                    <InputCurrency
                      currcode={
                        contract?.currcode ??
                        String(localStorage.getItem('currcode'))
                      }
                      onChange={(e) => {
                        handleinPPayoffAmountChange(e);
                      }}
                    />
                  </FormInput>
                  <FormInput
                    col
                    xs={8}
                    name="inPartialPayoffFormula"
                    wrapperCol={{ span: 24 }}
                  >
                    <PricingListFilteredSelect
                      //disabled={!getFieldValue('offerid')}
                      allowClear
                      disabled={state.isModSelected}
                      params={{
                        offerid: contract?.offerid ?? 0,
                        ctetype: 'PPOF', // Modifictaion fees
                        prcode: contract?.prcode ?? '',
                        entity: '',
                        entityid: 0,
                        tpid: contract?.tpidclient ?? 0,
                      }}
                      onChange={(e) => calculateTPOFFees(e)}
                    />
                  </FormInput>
                  <FormInput col xs={8} name="inPartialPayoffFeesAmount">
                    <InputCurrency
                      currcode={
                        contract?.currcode ??
                        String(localStorage.getItem('currcode'))
                      }
                      onChange={(e) => {
                        handleinPPayoffFeesAmountChange(e);
                      }}
                    />
                  </FormInput>
                </Row>
              </UtinaCard>

              <UtinaCard
                level={5}
                comcode="AddGracePeriod"
                styleCard={{
                  color: '#000',
                  paddingTop: 20,
                  paddingBottom: 25,
                }}
                lg={11}
              >
                <Row gutter={32}>
                  <FormInput
                    col
                    xs={5}
                    name="gpMethod"
                    rules={[{ required: true }]}
                  >
                    <ParListSelect
                      parcode="GPMETHOD"
                      onSelected={handleGpMethodChange}
                      disabled={state.isModSelected}
                    />
                  </FormInput>
                  <Form.Item
                    shouldUpdate={shouldUpdateCompare('gpMethod')}
                    noStyle
                  >
                    {({ getFieldValue }) =>
                      getFieldValue('gpMethod') !== 'NONE' && (
                        <>
                          <FormInput
                            col
                            xs={6}
                            name="gpDuration"
                            rules={[{ required: true }]}
                          >
                            <InputNumber
                              disabled={state.isModSelected}
                              onChange={handleGpDurationChange}
                            />
                          </FormInput>
                          <FormInput
                            col
                            xs={6}
                            name="gpPeriodicity"
                            rules={[{ required: true }]}
                          >
                            <ParListSelect
                              parcode="PERIOD"
                              onSelected={handleGpPeriodicityChange}
                              disabled={state.isModSelected}
                            />
                          </FormInput>

                          <FormInput
                            col
                            xs={6}
                            name="gpNominalRate"
                            rules={[{ required: true }]}
                          >
                            <InputRate
                              onChange={handleGpNominalRateChange}
                              disabled={state.isModSelected}
                            />
                          </FormInput>
                        </>
                      )
                    }
                  </Form.Item>
                </Row>
              </UtinaCard>
            </Row>

            <UtinaCard
              level={5}
              comcode="ChangeDurationandRate"
              styleCard={{
                color: '#000',
                paddingTop: 20,
                paddingBottom: 25,
              }}
              lg={24}
            >
              <Row gutter={32}>
                <Form.Item
                  shouldUpdate={shouldUpdateCompare('gpMethod')}
                  noStyle
                >
                  {({ getFieldValue }) =>
                    getFieldValue('gpMethod') !== 'NONE' && (
                      <FormInput
                        col
                        xs={5}
                        name="extendDuration"
                        rules={[{ required: true }]}
                      >
                        <Radio.Group
                          options={optionsExtend}
                          defaultValue="true"
                          optionType="button"
                          onChange={handleExtendDurationChange}
                          disabled={state.isModSelected}
                        />
                      </FormInput>
                    )
                  }
                </Form.Item>
                <FormInput
                  col
                  xs={12}
                  name="newRemainingDuration"
                  rules={[{ required: true }]}
                >
                  <SliderWithInput
                    disabled={
                      form?.getFieldValue('extendDuration') === false ||
                      state.isModSelected
                    }
                    min={durationMin ?? durationDefault}
                    max={durationMax ?? 100}
                    precision={0}
                    defaultValue={durationDefault}
                    onChange={handleNewRemainingDurationChange}
                  />
                </FormInput>

                <FormInput
                  col
                  xs={6}
                  name="newRate"
                  rules={[{ required: true }]}
                >
                  <InputRate
                    onChange={handleNewRateChange}
                    disabled={state.isModSelected}
                  />
                </FormInput>
                <FormInput
                  col
                  xs={6}
                  name="cteaprrate"
                >
                   <Text strong>
                    {toRate(state.cteaprrate)}
                  </Text>
                </FormInput>
              </Row>
            </UtinaCard>
            <br />
            <Row gutter={20}>
              <FormInput col xs={7} name="modreason">
                <ParListSelect parcode="MODREASON" />
              </FormInput>
              <FormInput
                col
                xs={17}
                name="comment"
                rules={[{ required: true }]}
              >
                <TextArea rows={3} />
              </FormInput>
            </Row>
          </TabPane>

          <TabPane tab={<GetLabel label="fvList" />} key={2} forceRender>
            <IrvScheduleTable
              listContextFV={fvList}
              irvSchedule={irvList ?? []}
              currcode={
                contract?.currcode ?? localStorage.getItem('currcode') ?? ''
              }
            />
          </TabPane>
          <TabPane tab={<GetLabel label="serviceList" />} key={3} forceRender>
            <Form.Item shouldUpdate>
              {({ getFieldValue }) => {
                const SERV: any[] = Array.isArray(
                  form.getFieldValue(['elements', 'SERV'])
                )
                  ? form.getFieldValue(['elements', 'SERV'])
                  : [];

                const FEES: any[] = Array.isArray(
                  form.getFieldValue(['elements', 'FEES'])
                )
                  ? form.getFieldValue(['elements', 'FEES'])
                  : [];

                const elements = [...SERV, ...FEES];
                const obj = {
                  finschedule: form.getFieldValue(
                    getName('conElemSchedules', elementID)
                  ),
                  ctegraceperiodduration: form.getFieldValue(
                    getName('ctegraceperiodduration', elementID)
                  ),
                  ctefinancedamount: form.getFieldValue(
                    getName('ctefinancedamount', elementID)
                  ),
                  cteinvestamount: form.getFieldValue(
                    getName('ctefinancedamount', elementID)
                  ),
                  assetamount: form.getFieldValue(
                    getName('ctefinancedamount', elementID)
                  ),
                  ctestartdate: form.getFieldValue(
                    getName('ctestartdate', elementID)
                  ),
                  cteduration: form.getFieldValue(
                    getName('cteduration', elementID)
                  ),
                };
                return (
                  <ElementsTable
                    offerid={getFieldValue('offerid')}
                    form={form}
                    elements={elements}
                    phase="PROD"
                    onConvertServToFinServ={()=>{}}
                    propsforcalculate={obj}
                    currcode={getFieldValue('currcode')}
                    onDeleteElement={()=>{}}
                  />
                );
              }}
            </Form.Item>
          </TabPane>
        </Tabs>
      </Spin>
    </DF.Provider>
  );
};
export default TabContractRescheduling;
